// THE PLAYBACK DATE PICKER SHOWS HOW MUCH HAPPENED ON EACH DAY (2026-10-13). Chris: "it's just binary, yes or no ... so I can gauge which days are of more interest to me than others."
// The count is the footer's own Sessions number for that day (the backend counts the same population), so the calendar and the footer agree. Pure helpers, tested.

// How strong a day's mark is, 0 (none) to 1 (the busiest day in view). Logarithmic: one 4,600-session day next to a 14-session day should still leave the quiet day visible, and a
// linear scale would draw it as nothing. A day with any sessions is never below MIN_LEVEL, so a day with 1 session still reads as "something happened".
export const MIN_LEVEL = 0.15;
export const dayLevel = (count: number, max: number): number => {
  if (!(count > 0) || !(max > 0)) {
    return 0;
  }
  if (max <= 1) {
    return 1;
  }
  const share = Math.log1p(Math.min(count, max)) / Math.log1p(max);

  return Math.max(MIN_LEVEL, Math.min(1, share));
};

// "812", "4.6k", "12k", "1.2M": short enough to sit inside a calendar day.
export const compactCount = (count: number): string => {
  const n = Math.max(0, Math.round(count));
  if (n < 1000) {
    return String(n);
  }
  if (n < 10_000) {
    return `${(Math.floor(n / 100) / 10).toFixed(1).replace(/\.0$/, "")}k`;
  }
  if (n < 1_000_000) {
    return `${Math.floor(n / 1000)}k`;
  }

  return `${(Math.floor(n / 100_000) / 10).toFixed(1).replace(/\.0$/, "")}M`;
};

export interface DayCountRow {
  day: string;
  sessions: number;
}

// rows from the server -> { "2026-10-07": 4607 }, ignoring anything malformed
export const toDayCountMap = (rows: unknown): Record<string, number> => {
  const out: Record<string, number> = {};
  if (!Array.isArray(rows)) {
    return out;
  }
  rows.forEach((row) => {
    if (row && typeof row.day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(row.day) && Number.isFinite(row.sessions) && row.sessions >= 0) {
      out[row.day] = Math.round(row.sessions);
    }
  });

  return out;
};

export const maxDayCount = (counts: Record<string, number>): number => Object.values(counts).reduce((max, n) => Math.max(max, n), 0);
